import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { appointmentsAPI, usersAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  User,
  Users,
  Bell,
  Send,
  RefreshCw,
  Filter,
  LayoutGrid,
  List,
  CalendarDays,
  Search,
  X,
  CheckCircle2,
  AlertCircle,
  Phone,
  Mail,
  MessageSquare,
} from 'lucide-react';

const STATUS_COLORS = {
  scheduled: 'bg-blue-500',
  confirmed: 'bg-green-500',
  checked_in: 'bg-purple-500',
  in_progress: 'bg-amber-500',
  completed: 'bg-gray-500',
  cancelled: 'bg-red-500',
  no_show: 'bg-red-700',
};

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  checked_in: 'Checked In',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
};

const DEFAULT_REMINDER_TIMINGS = ['48h', '24h', '2h'];

export default function SchedulingPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // day, week, month, agenda
  const [appointments, setAppointments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [reminderTimings, setReminderTimings] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [stats, setStats] = useState(null);
  
  // Dialog state
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showReminderManager, setShowReminderManager] = useState(false);
  
  // Form state
  const [newAppointment, setNewAppointment] = useState({
    patient_id: '',
    provider_id: '',
    appointment_date: '',
    appointment_time: '',
    duration_minutes: 30,
    appointment_type: 'general',
    reason: '',
    notes: '',
    reminder_config: { timing: DEFAULT_REMINDER_TIMINGS },
  });
  const [patientSearch, setPatientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Load appointments when date/provider changes
  useEffect(() => {
    loadAppointments();
  }, [currentDate, selectedProvider, view]);

  const loadData = async () => {
    try {
      // Load types and timings (these are always available)
      const [typesRes, timingsRes] = await Promise.all([
        appointmentsAPI.getTypes(),
        appointmentsAPI.getReminderTimings(),
      ]);
      
      setAppointmentTypes(typesRes.data);
      setReminderTimings(timingsRes.data);
      
      // Try to load users (requires users:read permission)
      try {
        const usersRes = await usersAPI.getAll();
        setProviders(usersRes.data.filter(u => ['physician', 'admin'].includes(u.role)));
      } catch (userError) {
        console.warn('Could not load providers:', userError);
        // Use current user as only provider option if can't load all users
        if (user) {
          setProviders([{ id: user.id, full_name: user.full_name, role: user.role }]);
        }
      }
      
      // Try to load stats
      try {
        const statsRes = await appointmentsAPI.getStats();
        setStats(statsRes.data);
      } catch (statsError) {
        console.warn('Could not load stats:', statsError);
      }
    } catch (error) {
      console.error('Failed to load scheduling data:', error);
      toast.error('Failed to load scheduling data');
    }
  };

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      const res = await appointmentsAPI.getCalendarEvents(
        startDate,
        endDate,
        selectedProvider !== 'all' ? selectedProvider : null
      );
      setAppointments(res.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    
    if (view === 'day') {
      // Single day
    } else if (view === 'week') {
      start.setDate(start.getDate() - start.getDay());
      end.setDate(start.getDate() + 6);
    } else if (view === 'month') {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    } else {
      // Agenda - next 7 days
      end.setDate(end.getDate() + 7);
    }
    
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Search patients
  const searchPatients = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await patientsAPI.getAll({ search: query, limit: 10 });
      setSearchResults(res.data);
    } catch (error) {
      console.error('Patient search failed:', error);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchPatients(patientSearch);
    }, 300);
    return () => clearTimeout(debounce);
  }, [patientSearch, searchPatients]);

  // Create appointment
  const handleCreateAppointment = async () => {
    if (!newAppointment.patient_id || !newAppointment.provider_id || 
        !newAppointment.appointment_date || !newAppointment.appointment_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await appointmentsAPI.create(newAppointment);
      toast.success('Appointment scheduled successfully');
      setShowNewAppointment(false);
      resetNewAppointment();
      loadAppointments();
      loadData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    }
  };

  const resetNewAppointment = () => {
    setNewAppointment({
      patient_id: '',
      provider_id: user?.id || '',
      appointment_date: '',
      appointment_time: '',
      duration_minutes: 30,
      appointment_type: 'general',
      reason: '',
      notes: '',
      reminder_config: { timing: DEFAULT_REMINDER_TIMINGS },
    });
    setPatientSearch('');
    setSearchResults([]);
  };

  // Update appointment status
  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      await appointmentsAPI.updateStatus(appointmentId, newStatus);
      toast.success(`Appointment ${STATUS_LABELS[newStatus].toLowerCase()}`);
      loadAppointments();
      setShowAppointmentDetail(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Send reminder manually
  const handleSendReminder = async (appointmentId) => {
    try {
      await appointmentsAPI.sendReminderNow(appointmentId);
      toast.success('Reminder sent successfully');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  // Format helpers - dd/mmm/yyyy format
  const formatDate = (date) => {
    // Handle date-only strings (YYYY-MM-DD) without timezone shift
    if (typeof date === 'string') {
      const str = date.split('T')[0];
      const parts = str.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        const day = d.getDate().toString().padStart(2, '0');
        const month = d.toLocaleString('en-US', { month: 'short' });
        const year = d.getFullYear();
        const weekday = d.toLocaleString('en-US', { weekday: 'long' });
        return `${weekday}, ${day}/${month}/${year}`;
      }
    }
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    const weekday = d.toLocaleString('en-US', { weekday: 'long' });
    return `${weekday}, ${day}/${month}/${year}`;
  };

  const formatDateShort = (date) => {
    if (typeof date === 'string') {
      const str = date.split('T')[0];
      const parts = str.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        const day = d.getDate().toString().padStart(2, '0');
        const month = d.toLocaleString('en-US', { month: 'short' });
        return `${day}/${month}`;
      }
    }
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    return `${day}/${month}`;
  };

  const getHeaderTitle = () => {
    if (view === 'day') {
      return formatDate(currentDate);
    } else if (view === 'week') {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${formatDateShort(start)} - ${formatDateShort(end)}, ${start.getFullYear()}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  // Click on appointment
  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetail(true);
  };

  // Group appointments by date for agenda view
  const getGroupedAppointments = () => {
    const grouped = {};
    appointments.forEach(apt => {
      const date = apt.start.split('T')[0];
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(apt);
    });
    return grouped;
  };

  // Render time slots for day/week view
  const renderTimeSlots = () => {
    const hours = [];
    for (let i = 7; i <= 19; i++) {
      hours.push(i);
    }
    return hours;
  };

  // Get appointments for a specific day and hour
  const getAppointmentsForSlot = (date, hour) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => {
      const aptDate = apt.start.split('T')[0];
      const aptHour = parseInt(apt.start.split('T')[1].split(':')[0]);
      return aptDate === dateStr && aptHour === hour;
    });
  };

  // Generate week days
  const getWeekDays = () => {
    const days = [];
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  return (
    <div className="space-y-6" data-testid="scheduling-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Scheduling</h1>
          <p className="text-slate-500">Manage appointments and reminders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowReminderManager(true)} data-testid="reminder-manager-btn">
            <Bell className="h-4 w-4 mr-2" />
            Reminders
          </Button>
          {hasPermission('appointments:create') && (
            <Button onClick={() => setShowNewAppointment(true)} data-testid="new-appointment-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Appointment
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Today</p>
                  <p className="text-2xl font-bold">{stats.today?.total || 0}</p>
                </div>
                <CalendarDays className="h-8 w-8 text-slate-500" />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {stats.today?.completed || 0} completed, {stats.today?.remaining || 0} remaining
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">This Month</p>
                  <p className="text-2xl font-bold">{stats.total_appointments || 0}</p>
                </div>
                <Calendar className="h-8 w-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Confirmed</p>
                  <p className="text-2xl font-bold">{stats.status_breakdown?.confirmed || 0}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Reminders Sent</p>
                  <p className="text-2xl font-bold">{stats.reminders_sent || 0}</p>
                </div>
                <Bell className="h-8 w-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendar Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateDate(-1)} data-testid="prev-date-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={goToToday}>Today</Button>
              <Button variant="outline" size="icon" onClick={() => navigateDate(1)} data-testid="next-date-btn">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <span className="font-medium ml-2">{getHeaderTitle()}</span>
            </div>

            {/* View Toggle & Filters */}
            <div className="flex items-center gap-2">
              <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                <SelectTrigger className="w-[180px]" data-testid="provider-filter">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Providers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Providers</SelectItem>
                  {providers.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Tabs value={view} onValueChange={setView} className="ml-2">
                <TabsList>
                  <TabsTrigger value="day" data-testid="view-day"><Clock className="h-4 w-4" /></TabsTrigger>
                  <TabsTrigger value="week" data-testid="view-week"><LayoutGrid className="h-4 w-4" /></TabsTrigger>
                  <TabsTrigger value="month" data-testid="view-month"><Calendar className="h-4 w-4" /></TabsTrigger>
                  <TabsTrigger value="agenda" data-testid="view-agenda"><List className="h-4 w-4" /></TabsTrigger>
                </TabsList>
              </Tabs>

              <Button variant="ghost" size="icon" onClick={loadAppointments}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar View */}
      <Card className="min-h-[600px]">
        <CardContent className="p-0">
          {view === 'agenda' ? (
            <AgendaView 
              groupedAppointments={getGroupedAppointments()} 
              onAppointmentClick={handleAppointmentClick}
              formatDate={formatDate}
            />
          ) : view === 'month' ? (
            <MonthView 
              currentDate={currentDate}
              appointments={appointments}
              onAppointmentClick={handleAppointmentClick}
              onDateClick={(date) => {
                setCurrentDate(date);
                setView('day');
              }}
            />
          ) : (
            <WeekDayView
              view={view}
              currentDate={currentDate}
              appointments={appointments}
              weekDays={getWeekDays()}
              timeSlots={renderTimeSlots()}
              getAppointmentsForSlot={getAppointmentsForSlot}
              onAppointmentClick={handleAppointmentClick}
              onSlotClick={(date, hour) => {
                const dateStr = date.toISOString().split('T')[0];
                const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                setNewAppointment(prev => ({
                  ...prev,
                  appointment_date: dateStr,
                  appointment_time: timeStr,
                  provider_id: selectedProvider !== 'all' ? selectedProvider : user?.id || '',
                }));
                setShowNewAppointment(true);
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* New Appointment Dialog */}
      <Dialog open={showNewAppointment} onOpenChange={setShowNewAppointment}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule New Appointment</DialogTitle>
            <DialogDescription>Create a new appointment and configure reminders</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Patient Search */}
            <div className="space-y-2">
              <Label>Patient *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  placeholder="Search patients..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="pl-9"
                  data-testid="patient-search"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="border rounded-md max-h-40 overflow-y-auto">
                  {searchResults.map(p => (
                    <div
                      key={p.id}
                      className="p-2 hover:bg-muted cursor-pointer flex justify-between items-center"
                      onClick={() => {
                        setNewAppointment(prev => ({ ...prev, patient_id: p.id }));
                        setPatientSearch(`${p.first_name} ${p.last_name}`);
                        setSearchResults([]);
                      }}
                    >
                      <span>{p.first_name} {p.last_name}</span>
                      <span className="text-xs text-slate-500">{p.patient_id}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Provider */}
            <div className="space-y-2">
              <Label>Provider *</Label>
              <Select
                value={newAppointment.provider_id}
                onValueChange={(v) => setNewAppointment(prev => ({ ...prev, provider_id: v }))}
              >
                <SelectTrigger data-testid="provider-select">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newAppointment.appointment_date}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, appointment_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="appointment-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={newAppointment.appointment_time}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, appointment_time: e.target.value }))}
                  data-testid="appointment-time"
                />
              </div>
            </div>

            {/* Appointment Type & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newAppointment.appointment_type}
                  onValueChange={(v) => {
                    const type = appointmentTypes.find(t => t.value === v);
                    setNewAppointment(prev => ({
                      ...prev,
                      appointment_type: v,
                      duration_minutes: type?.duration || prev.duration_minutes,
                    }));
                  }}
                >
                  <SelectTrigger data-testid="appointment-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                          {t.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duration (min)</Label>
                <Select
                  value={newAppointment.duration_minutes.toString()}
                  onValueChange={(v) => setNewAppointment(prev => ({ ...prev, duration_minutes: parseInt(v) }))}
                >
                  <SelectTrigger data-testid="duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">60 min</SelectItem>
                    <SelectItem value="90">90 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason for Visit</Label>
              <Input
                value={newAppointment.reason}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Brief reason for appointment"
                data-testid="appointment-reason"
              />
            </div>

            {/* Reminder Settings */}
            <div className="space-y-2">
              <Label>Reminder Timing</Label>
              <div className="flex flex-wrap gap-2">
                {reminderTimings.map(t => (
                  <div key={t.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`reminder-${t.value}`}
                      checked={newAppointment.reminder_config.timing.includes(t.value)}
                      onCheckedChange={(checked) => {
                        setNewAppointment(prev => ({
                          ...prev,
                          reminder_config: {
                            ...prev.reminder_config,
                            timing: checked
                              ? [...prev.reminder_config.timing, t.value]
                              : prev.reminder_config.timing.filter(x => x !== t.value)
                          }
                        }));
                      }}
                    />
                    <label htmlFor={`reminder-${t.value}`} className="text-sm">{t.label}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAppointment(false)}>Cancel</Button>
            <Button onClick={handleCreateAppointment} data-testid="create-appointment-btn">
              Schedule Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Detail Dialog */}
      <Dialog open={showAppointmentDetail} onOpenChange={setShowAppointmentDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
            <DialogDescription>View and manage appointment information</DialogDescription>
          </DialogHeader>

          {selectedAppointment && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <User className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-medium">{selectedAppointment.patient_name}</p>
                  <p className="text-sm text-slate-500">{selectedAppointment.appointment_type}</p>
                </div>
                <Badge className={`ml-auto ${STATUS_COLORS[selectedAppointment.status]}`}>
                  {STATUS_LABELS[selectedAppointment.status]}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Date</p>
                  <p className="font-medium">{formatDate(selectedAppointment.start.split('T')[0])}</p>
                </div>
                <div>
                  <p className="text-slate-500">Time</p>
                  <p className="font-medium">
                    {selectedAppointment.start.split('T')[1].substring(0, 5)} - 
                    {selectedAppointment.end.split('T')[1].substring(0, 5)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Provider</p>
                  <p className="font-medium">{selectedAppointment.provider_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Type</p>
                  <p className="font-medium capitalize">{selectedAppointment.appointment_type}</p>
                </div>
              </div>

              {/* Status Actions */}
              <div className="space-y-2">
                <Label>Update Status</Label>
                <div className="flex flex-wrap gap-2">
                  {['confirmed', 'checked_in', 'in_progress', 'completed', 'cancelled', 'no_show'].map(status => (
                    <Button
                      key={status}
                      variant={selectedAppointment.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleStatusUpdate(selectedAppointment.id, status)}
                      disabled={selectedAppointment.status === status}
                    >
                      {STATUS_LABELS[status]}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Send Reminder */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleSendReminder(selectedAppointment.id)}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Reminder
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/patients/${selectedAppointment.patient_id}`)}
                >
                  View Patient
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reminder Manager Dialog */}
      <ReminderManagerDialog
        open={showReminderManager}
        onClose={() => setShowReminderManager(false)}
      />
    </div>
  );
}

// Agenda View Component
function AgendaView({ groupedAppointments, onAppointmentClick, formatDate }) {
  const dates = Object.keys(groupedAppointments).sort();
  
  if (dates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-slate-500">
        <Calendar className="h-12 w-12 mb-4" />
        <p>No appointments scheduled</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[600px]">
      <div className="p-4 space-y-6">
        {dates.map(date => (
          <div key={date}>
            <h3 className="font-medium mb-3 sticky top-0 bg-background py-2">
              {formatDate(date)}
            </h3>
            <div className="space-y-2">
              {groupedAppointments[date].map(apt => (
                <div
                  key={apt.id}
                  className="flex items-center gap-4 p-3 rounded-lg border hover:bg-muted cursor-pointer transition-colors"
                  onClick={() => onAppointmentClick(apt)}
                  data-testid={`agenda-appointment-${apt.id}`}
                >
                  <div className={`w-1 h-12 rounded-full ${STATUS_COLORS[apt.status]}`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{apt.patient_name}</p>
                      <Badge variant="outline" className="text-xs">{apt.appointment_type}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {apt.start.split('T')[1].substring(0, 5)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {apt.provider_name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

// Week/Day View Component
function WeekDayView({ view, currentDate, appointments, weekDays, timeSlots, getAppointmentsForSlot, onAppointmentClick, onSlotClick }) {
  const days = view === 'day' ? [currentDate] : weekDays;
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="overflow-auto">
      <div className="min-w-[800px]">
        {/* Header */}
        <div className="grid border-b sticky top-0 bg-background z-10" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
          <div className="p-2 border-r" />
          {days.map((day, idx) => {
            const dateStr = day.toISOString().split('T')[0];
            const isToday = dateStr === today;
            return (
              <div key={idx} className={`p-2 text-center border-r ${isToday ? 'bg-primary/10' : ''}`}>
                <p className="text-xs text-slate-500">
                  {day.toLocaleDateString('en-US', { weekday: 'short' })}
                </p>
                <p className={`text-lg font-semibold ${isToday ? 'text-primary' : ''}`}>
                  {day.getDate()}
                </p>
              </div>
            );
          })}
        </div>

        {/* Time Grid */}
        <div className="relative">
          {timeSlots.map(hour => (
            <div key={hour} className="grid" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
              <div className="p-2 text-xs text-slate-500 text-right border-r h-16">
                {hour.toString().padStart(2, '0')}:00
              </div>
              {days.map((day, idx) => {
                const slotAppointments = getAppointmentsForSlot(day, hour);
                return (
                  <div
                    key={idx}
                    className="border-r border-b h-16 relative hover:bg-muted/50 cursor-pointer"
                    onClick={() => onSlotClick(day, hour)}
                    data-testid={`time-slot-${day.toISOString().split('T')[0]}-${hour}`}
                  >
                    {slotAppointments.map(apt => (
                      <div
                        key={apt.id}
                        className={`absolute inset-x-1 top-1 p-1 rounded text-xs text-white cursor-pointer z-10 ${STATUS_COLORS[apt.status]}`}
                        style={{ minHeight: '24px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick(apt);
                        }}
                        data-testid={`calendar-appointment-${apt.id}`}
                      >
                        <p className="font-medium truncate">{apt.patient_name}</p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Month View Component
function MonthView({ currentDate, appointments, onAppointmentClick, onDateClick }) {
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startOffset = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const today = new Date().toISOString().split('T')[0];

  // Get appointments count by date
  const appointmentsByDate = {};
  appointments.forEach(apt => {
    const date = apt.start.split('T')[0];
    if (!appointmentsByDate[date]) appointmentsByDate[date] = [];
    appointmentsByDate[date].push(apt);
  });

  const weeks = [];
  let days = [];
  
  // Empty cells before first day
  for (let i = 0; i < startOffset; i++) {
    days.push(null);
  }
  
  // Days of month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
    if (days.length === 7) {
      weeks.push(days);
      days = [];
    }
  }
  
  // Empty cells after last day
  if (days.length > 0) {
    while (days.length < 7) days.push(null);
    weeks.push(days);
  }

  return (
    <div className="p-4">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-slate-500">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="border rounded-lg overflow-hidden">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7">
            {week.map((day, dayIdx) => {
              if (!day) return <div key={dayIdx} className="h-24 border-r border-b bg-muted/20" />;
              
              const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
              const isToday = dateStr === today;
              const dayAppointments = appointmentsByDate[dateStr] || [];
              
              return (
                <div
                  key={dayIdx}
                  className={`h-24 border-r border-b p-1 cursor-pointer hover:bg-muted/50 ${isToday ? 'bg-primary/5' : ''}`}
                  onClick={() => onDateClick(new Date(dateStr))}
                  data-testid={`month-day-${dateStr}`}
                >
                  <p className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>{day}</p>
                  <div className="space-y-0.5 mt-1">
                    {dayAppointments.slice(0, 3).map(apt => (
                      <div
                        key={apt.id}
                        className={`text-xs p-0.5 rounded truncate text-white ${STATUS_COLORS[apt.status]}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick(apt);
                        }}
                      >
                        {apt.start.split('T')[1].substring(0, 5)} {apt.patient_name.split(' ')[0]}
                      </div>
                    ))}
                    {dayAppointments.length > 3 && (
                      <p className="text-xs text-slate-500">+{dayAppointments.length - 3} more</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// Reminder Manager Dialog Component
function ReminderManagerDialog({ open, onClose }) {
  const [activeTab, setActiveTab] = useState('pending');
  const [reminders, setReminders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'pending') {
        const res = await appointmentsAPI.getPendingReminders();
        setReminders(res.data);
      } else {
        const res = await appointmentsAPI.getCommunicationLogs({ limit: 50 });
        setLogs(res.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Reminder Management</DialogTitle>
          <DialogDescription>View pending reminders and communication history</DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="pending" className="flex-1">
              <Bell className="h-4 w-4 mr-2" />
              Pending Reminders
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1">
              <MessageSquare className="h-4 w-4 mr-2" />
              Communication History
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[400px] mt-4">
            <TabsContent value="pending" className="mt-0">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : reminders.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No pending reminders</div>
              ) : (
                <div className="space-y-2">
                  {reminders.map(r => (
                    <div key={r.id} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{r.patient_name}</p>
                          <p className="text-sm text-slate-500">
                            Scheduled: {new Date(r.scheduled_time).toLocaleString()}
                          </p>
                        </div>
                        <Badge variant="outline">{r.timing_code}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="history" className="mt-0">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No communication history</div>
              ) : (
                <div className="space-y-2">
                  {logs.map(log => (
                    <div key={log.id} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          {log.communication_type === 'sms' ? (
                            <Phone className="h-4 w-4 text-slate-500" />
                          ) : (
                            <Mail className="h-4 w-4 text-slate-500" />
                          )}
                          <div>
                            <p className="font-medium">{log.patient_name}</p>
                            <p className="text-xs text-slate-500">{log.recipient}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                            {log.status}
                          </Badge>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(log.sent_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm mt-2 text-slate-500 line-clamp-2">{log.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
